import React, { useState, useEffect, useCallback } from "react";
import { API_BASE, authHeaders } from "./AgencyContext";

const TYPE_LABEL = {
  content_approval: "Content Approval",
  posting_schedule: "Posting Schedule",
  client_onboarding: "Client Onboarding",
  reporting: "Reporting",
};

const TYPE_DESC = {
  content_approval: "Define approval chains for client posts before they go live.",
  posting_schedule: "Set default posting schedules for new clients.",
  client_onboarding: "Automated steps when a new client joins.",
  reporting: "Scheduled reports sent to clients or agency admins.",
};

export default function AgencyWorkflows() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", workflow_type: "content_approval", description: "", is_default: false });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/workflows`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        setWorkflows(d.workflows || []);
      }
    } catch { /* ignore */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSubmitting(true);
    setMsg(null);
    try {
      const r = await fetch(`${API_BASE}/app/api/agency/workflows`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          ...form,
          steps: [],
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: "ok", text: `Workflow "${form.name}" created` });
        setForm({ name: "", workflow_type: "content_approval", description: "", is_default: false });
        setShowCreate(false);
        load();
      } else {
        setMsg({ type: "err", text: d.error || "Failed" });
      }
    } catch {
      setMsg({ type: "err", text: "Network error" });
    }
    setSubmitting(false);
  }

  async function toggleActive(wf) {
    try {
      await fetch(`${API_BASE}/app/api/agency/workflows/${wf.id}`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ is_active: !wf.is_active }),
      });
      load();
    } catch { /* ignore */ }
  }

  async function deleteWorkflow(wf) {
    if (!window.confirm(`Delete workflow "${wf.name}"?`)) return;
    try {
      await fetch(`${API_BASE}/app/api/agency/workflows/${wf.id}`, {
        method: "DELETE", headers: authHeaders(),
      });
      load();
    } catch { /* ignore */ }
  }

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 500 }}>Workflows</div>
          <div style={{ fontSize: 13, color: "#888" }}>Automate content approval, scheduling, and onboarding</div>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          style={{ background: "#0077B5", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          + New Workflow
        </button>
      </div>

      {msg && (
        <div style={{ padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.type === "ok" ? "#E1F5EE" : "#FAE4E4", color: msg.type === "ok" ? "#0F6E56" : "#8B1A1A" }}>
          {msg.text}
        </div>
      )}

      {showCreate && (
        <form onSubmit={create} style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Create workflow</div>
          <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
            <input placeholder="Workflow name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }} />
            <select value={form.workflow_type} onChange={e => setForm({ ...form, workflow_type: e.target.value })}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13 }}>
              {Object.entries(TYPE_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <textarea placeholder="Description (optional)" value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #ddd", fontSize: 13, resize: "vertical" }} />
            <label style={{ fontSize: 12, color: "#555", display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} />
              Set as default for this type
            </label>
          </div>
          <button type="submit" disabled={submitting}
            style={{ background: "#0077B5", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
            {submitting ? "Creating..." : "Create Workflow"}
          </button>
        </form>
      )}

      {workflows.length === 0 ? (
        <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 15, color: "#888", marginBottom: 12 }}>No workflows yet</div>
          <div style={{ fontSize: 13, color: "#aaa", maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>
            Create workflows to automate content approval chains, posting schedules, client onboarding steps, and reporting.
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {workflows.map(wf => (
            <div key={wf.id} style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, overflow: "hidden" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", cursor: "pointer" }}
                onClick={() => setExpanded(expanded === wf.id ? null : wf.id)}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{wf.name}</div>
                    <div style={{ fontSize: 11, color: "#888" }}>{TYPE_LABEL[wf.workflow_type] || wf.workflow_type}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {wf.is_default && (
                    <span style={{ fontSize: 10, background: "#E6F1FB", color: "#0C447C", padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>Default</span>
                  )}
                  <span style={{ fontSize: 11, background: wf.is_active ? "#E1F5EE" : "#f3f2ef", color: wf.is_active ? "#0F6E56" : "#888", padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>
                    {wf.is_active ? "Active" : "Inactive"}
                  </span>
                  <span style={{ fontSize: 12, color: "#888" }}>{expanded === wf.id ? "−" : "+"}</span>
                </div>
              </div>

              {expanded === wf.id && (
                <div style={{ padding: "0 16px 14px", borderTop: "0.5px solid rgba(0,0,0,0.05)" }}>
                  {wf.description && <div style={{ fontSize: 13, color: "#555", margin: "12px 0 8px", lineHeight: 1.5 }}>{wf.description}</div>}
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                    Type: {TYPE_LABEL[wf.workflow_type]}
                    {wf.apply_to_all_clients && " — Applied to all clients"}
                  </div>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                    {TYPE_DESC[wf.workflow_type]}
                  </div>
                  <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                    Steps: {Array.isArray(wf.steps) ? wf.steps.length : 0} configured
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => toggleActive(wf)}
                      style={{ background: "none", border: "1px solid #ddd", padding: "4px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer", color: "#555" }}>
                      {wf.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button onClick={() => deleteWorkflow(wf)}
                      style={{ background: "none", border: "1px solid #FAE4E4", padding: "4px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer", color: "#D85A30" }}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
